import type { AppLanguage } from '@/lib/prefs/usePrefsStore';

/**
 * Product tour catalogue — bilingual, one entry per main screen.
 *
 * Mirrors the Help Center pattern (`lib/help/helpContent.ts`): structured
 * content lives here, NOT in `translations.ts`. Each step optionally anchors to
 * a DOM element via a `[data-tour="…"]` selector; steps whose anchor is missing
 * at runtime are skipped, so a tour degrades gracefully on slow data or
 * permission-gated UI. Element-less steps render as a centered card (used for
 * the welcome / closing beats).
 *
 * Copy is action-led: each step says what the thing is AND what you do with it,
 * so the tour actually teaches the workflow (add products, invite reporters,
 * configure the survey/game, set up stations) instead of just labelling the UI.
 * The Help Center (`/help`) carries the long-form depth.
 */

type L10n = { en: string; es: string };

export interface TourStepDef {
  /** CSS selector for the anchor. Omit for a centered, element-less step. */
  element?: string;
  title: L10n;
  description: L10n;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
}

export interface TourDef {
  id: string;
  steps: TourStepDef[];
}

/** Resolve a step's localized strings into driver.js popover shape. */
export function localizeStep(step: TourStepDef, lang: AppLanguage) {
  return {
    element: step.element,
    popover: {
      title: step.title[lang],
      description: step.description[lang],
      side: step.side,
      align: step.align,
    },
  };
}

export const TOURS: Record<string, TourDef> = {
  home: {
    id: 'home',
    steps: [
      {
        title: { en: 'Welcome to Xotski 👋', es: 'Te damos la bienvenida a Xotski 👋' },
        description: {
          en: 'A quick tour of your workspace. Every page has its own walkthrough — replay any of them from the compass icon in the top bar.',
          es: 'Un recorrido rápido por tu espacio de trabajo. Cada página tiene su propia guía — repítelas cuando quieras desde el icono de brújula en la barra superior.',
        },
      },
      {
        element: '[data-tour="nav-brand-projects"]',
        title: { en: 'Your navigation', es: 'Tu navegación' },
        description: {
          en: 'The sidebar is your map: projects & events, your product catalog, stations, surveys, insights and settings.',
          es: 'La barra lateral es tu mapa: proyectos y eventos, tu catálogo de productos, estaciones, encuestas, métricas y ajustes.',
        },
        side: 'right',
        align: 'start',
      },
      {
        element: '[data-tour="quick-add-project"]',
        title: { en: 'Start a campaign', es: 'Inicia una campaña' },
        description: {
          en: 'A project groups related events. Create one first — it sets the date range every event must fall within.',
          es: 'Un proyecto agrupa eventos relacionados. Crea uno primero — define el rango de fechas dentro del cual deben caer todos los eventos.',
        },
        side: 'top',
      },
      {
        element: '[data-tour="quick-add-events"]',
        title: { en: 'Add events', es: 'Agrega eventos' },
        description: {
          en: 'An event is a single activation: a date, a venue and one or more stations. Each station runs a survey that ends in a prize game.',
          es: 'Un evento es una activación: una fecha, una sede y una o más estaciones. Cada estación corre una encuesta que termina en un juego de premios.',
        },
        side: 'top',
      },
      {
        element: '[data-tour="topbar-search"]',
        title: { en: 'Find anything', es: 'Encuentra lo que sea' },
        description: {
          en: 'Jump to any event, brand, station or product instantly — no clicking through menus.',
          es: 'Salta a cualquier evento, marca, estación o producto al instante — sin navegar por menús.',
        },
        side: 'bottom',
      },
      {
        element: '[data-tour="topbar-workspace"]',
        title: { en: 'Switch workspace', es: 'Cambia de espacio' },
        description: {
          en: 'Manage several brands or agencies? Switch the active workspace here — all data scopes to it automatically.',
          es: '¿Gestionas varias marcas o agencias? Cambia el espacio activo aquí — todos los datos se ajustan a él automáticamente.',
        },
        side: 'bottom',
        align: 'start',
      },
      {
        element: '[data-tour="topbar-help"]',
        title: { en: "You're all set", es: 'Todo listo' },
        description: {
          en: 'Open any project or page and its tour will guide you through the real workflow. Enjoy Xotski!',
          es: 'Abre cualquier proyecto o página y su recorrido te guiará por el flujo real. ¡Disfruta Xotski!',
        },
        side: 'bottom',
      },
    ],
  },

  projects: {
    id: 'projects',
    steps: [
      {
        title: { en: 'Projects', es: 'Proyectos' },
        description: {
          en: 'Every campaign you run lives here. A project is the container for a set of events.',
          es: 'Cada campaña que ejecutas vive aquí. Un proyecto es el contenedor de un conjunto de eventos.',
        },
      },
      {
        element: '[data-tour="projects-create"]',
        title: { en: 'New project', es: 'Nuevo proyecto' },
        description: {
          en: 'Give it a name and a start/end date range (by calendar day). That range governs the dates allowed on every event inside it.',
          es: 'Dale un nombre y un rango de fechas de inicio/fin (por día de calendario). Ese rango rige las fechas permitidas en cada evento que contenga.',
        },
        side: 'bottom',
      },
      {
        element: '[data-tour="projects-list"]',
        title: { en: 'Your campaigns', es: 'Tus campañas' },
        description: {
          en: 'Each card shows a project’s status and event count. Open one to plan its events, allocate products and measure results.',
          es: 'Cada tarjeta muestra el estado y el número de eventos de un proyecto. Abre uno para planificar sus eventos, asignar productos y medir resultados.',
        },
        side: 'top',
      },
    ],
  },

  'project-detail': {
    id: 'project-detail',
    steps: [
      {
        title: { en: 'Plan a campaign', es: 'Planifica una campaña' },
        description: {
          en: 'This is your cockpit. Let’s walk the whole flow: events → products → stations → reporters → the survey and games.',
          es: 'Este es tu centro de mando. Recorramos el flujo completo: eventos → productos → estaciones → reporteros → la encuesta y los juegos.',
        },
      },
      {
        element: '[data-tour="project-tabs"]',
        title: { en: 'Plan · Track · Measure', es: 'Planificar · Seguir · Medir' },
        description: {
          en: 'PLAN is this drag-and-drop board. TRACK groups events by phase (live / upcoming / done). MEASURE shows campaign-wide stats and charts.',
          es: 'PLANIFICAR es este tablero de arrastrar y soltar. SEGUIR agrupa eventos por fase (en vivo / próximos / terminados). MEDIR muestra estadísticas y gráficas de toda la campaña.',
        },
        side: 'bottom',
      },
      {
        element: '[data-tour="project-add-events"]',
        title: { en: '1 · Create events', es: '1 · Crea eventos' },
        description: {
          en: 'Start here. Batch-create several events at once — they’re auto-spread across the project’s date range, each born with one station.',
          es: 'Empieza aquí. Crea varios eventos a la vez — se reparten automáticamente en el rango de fechas del proyecto, cada uno con una estación.',
        },
        side: 'left',
      },
      {
        element: '[data-tour="planner-xotskis"]',
        title: { en: '2 · Your products (merch)', es: '2 · Tus productos (merch)' },
        description: {
          en: 'These are your prizes & samples. Drag one onto an event to stock it across that event’s stations. Manage the full catalog under “My Merch”.',
          es: 'Estos son tus premios y muestras. Arrastra uno sobre un evento para asignarlo a las estaciones de ese evento. Gestiona el catálogo completo en “My Merch”.',
        },
        side: 'bottom',
        align: 'start',
      },
      {
        element: '[data-tour="planner-stations"]',
        title: { en: '3 · Stations', es: '3 · Estaciones' },
        description: {
          en: 'A station is a physical touchpoint — a QR + the prize game. Drag this onto an event to add another station; every event needs at least one.',
          es: 'Una estación es un punto de contacto físico — un QR + el juego de premios. Arrastra esto sobre un evento para añadir otra estación; cada evento necesita al menos una.',
        },
        side: 'bottom',
      },
      {
        element: '[data-tour="planner-events"]',
        title: { en: '4 · Events board & the game', es: '4 · Tablero de eventos y el juego' },
        description: {
          en: 'Each card is an event — drop products, stations and reporters onto it. Expand a station inside a card to open the slice editor: set each prize’s quantity, drag to reorder (that order IS the game order) and preview the game.',
          es: 'Cada tarjeta es un evento — suelta productos, estaciones y reporteros sobre ella. Expande una estación dentro de una tarjeta para abrir el editor de slices: ajusta la cantidad de cada premio, arrastra para reordenar (ese orden ES el del juego) y previsualízala.',
        },
        side: 'right',
        align: 'start',
      },
      {
        element: '[data-tour="planner-reporters"]',
        title: { en: '5 · Reporters', es: '5 · Reporteros' },
        description: {
          en: 'Invite field staff by email — they receive a magic link to run a station on-site, no account needed. Then drag a reporter onto the event they’ll work.',
          es: 'Invita a tu personal de campo por correo — reciben un enlace mágico para operar una estación en el lugar, sin necesidad de cuenta. Luego arrastra un reportero al evento que cubrirá.',
        },
        side: 'left',
        align: 'start',
      },
      {
        element: '[data-tour="event-properties"]',
        title: { en: '6 · Survey & details', es: '6 · Encuesta y detalles' },
        description: {
          en: 'Select an event to edit its name, address and exact date/time, and pick the Journey template — the survey-to-game experience guests go through at every station.',
          es: 'Selecciona un evento para editar su nombre, dirección y fecha/hora exactas, y elegir la plantilla de Journey — la experiencia de encuesta a juego que viven los invitados en cada estación.',
        },
        side: 'left',
      },
      {
        element: '[data-tour="project-edit"]',
        title: { en: 'Edit the project', es: 'Edita el proyecto' },
        description: {
          en: 'Rename it, change its status, or adjust the date range that bounds its events — anytime.',
          es: 'Renómbralo, cambia su estado o ajusta el rango de fechas que acota sus eventos — cuando quieras.',
        },
        side: 'bottom',
        align: 'end',
      },
    ],
  },

  'event-detail': {
    id: 'event-detail',
    steps: [
      {
        title: { en: 'Set up an event', es: 'Configura un evento' },
        description: {
          en: 'Everything for one activation: stations, products, reporters, the guest journey and live results.',
          es: 'Todo para una activación: estaciones, productos, reporteros, el journey del invitado y resultados en vivo.',
        },
      },
      {
        element: '[data-tour="event-tabs"]',
        title: { en: 'Event tabs', es: 'Pestañas del evento' },
        description: {
          en: 'PLAN sets up stations & inventory. DESIGN JOURNEY builds the survey + game. TRACK watches it live. REPORT is the post-event recap.',
          es: 'PLANIFICAR configura estaciones e inventario. DESIGN JOURNEY construye la encuesta + juego. SEGUIR la observa en vivo. REPORTE es el resumen posterior.',
        },
        side: 'bottom',
      },
      {
        element: '[data-tour="planner-xotskis"]',
        title: { en: 'Stock the event', es: 'Abastece el evento' },
        description: {
          en: 'Drag products onto a station to load its game. Expand a station to fine-tune each prize’s quantity and its order in the game.',
          es: 'Arrastra productos a una estación para cargar su juego. Expande una estación para afinar la cantidad de cada premio y su orden en el juego.',
        },
        side: 'bottom',
        align: 'start',
      },
      {
        element: '[data-tour="planner-reporters"]',
        title: { en: 'Assign reporters', es: 'Asigna reporteros' },
        description: {
          en: 'Invite and assign the field staff who will run this event’s stations. Each gets a secure magic link.',
          es: 'Invita y asigna al personal de campo que operará las estaciones de este evento. Cada uno recibe un enlace mágico seguro.',
        },
        side: 'left',
        align: 'start',
      },
      {
        element: '[data-tour="event-properties"]',
        title: { en: 'Properties & journey', es: 'Propiedades y journey' },
        description: {
          en: 'Name, address, exact start/end date & time (clamped to the project range), and the Journey template applied to this event.',
          es: 'Nombre, dirección, fecha y hora exactas de inicio/fin (acotadas al rango del proyecto) y la plantilla de Journey aplicada a este evento.',
        },
        side: 'left',
      },
    ],
  },

  /**
   * El Design Journey: el panel que viste una encuesta para UN evento.
   *
   * Es el recorrido más largo del producto y con razón — es donde más gente se
   * pierde, porque lo que se toca aquí NO es la encuesta: son los ajustes de su
   * despliegue. Sin entender esa frase, cada paso siguiente se interpreta mal.
   */
  'design-journey': {
    id: 'design-journey',
    steps: [
      {
        element: '[data-tour="design-journey"]',
        title: { en: 'Design Journey', es: 'Design Journey' },
        description: {
          en: 'Here you dress the survey for THIS event. You are not editing the survey — you are editing its deployment, so the same survey can run at ten events wearing ten looks and its answers stay comparable across all of them.',
          es: 'Aquí vistes la encuesta para ESTE evento. No estás editando la encuesta: estás editando su despliegue, así que la misma encuesta puede correr en diez eventos con diez apariencias y sus respuestas siguen siendo comparables entre todos.',
        },
      },
      {
        element: '[data-tour="dj-steps"]',
        title: { en: 'The steps, and what to hide', es: 'Los pasos y qué ocultar' },
        description: {
          en: 'Every step of the survey, in the order the guest sees them. Reorder them, hide a whole step, or open one and hide a single question with its eye. A hidden required question stops blocking “continue”, and a step whose questions are all hidden disappears on its own — it tells you so rather than leaving you a dead screen.',
          es: 'Cada paso de la encuesta, en el orden en que los ve el invitado. Reordénalos, oculta un paso entero, o abre uno y oculta una pregunta suelta con su ojo. Una pregunta obligatoria oculta deja de bloquear el «continuar», y el paso al que se le ocultan todas desaparece solo — te lo dice, en vez de dejarte una pantalla muerta.',
        },
        side: 'right',
        align: 'start',
      },
      {
        element: '[data-tour="dj-preview"]',
        title: { en: 'The phone is the real thing', es: 'El móvil es de verdad' },
        description: {
          en: 'Not a mock-up: it is the station’s public link, the same one the QR carries, opened in preview mode. Nothing is saved and no stock moves, so you can play the game as many times as you like. What does not fit here will not fit in a real phone either.',
          es: 'No es una maqueta: es el enlace público de la estación, el mismo que lleva el QR, abierto en modo previa. No se guarda nada ni se mueve stock, así que puedes jugar las veces que quieras. Lo que no quepa aquí tampoco cabrá en un móvil de verdad.',
        },
        side: 'left',
      },
      {
        element: '[data-tour="dj-presets"]',
        title: { en: 'Saved styles', es: 'Estilos guardados' },
        description: {
          en: 'Design it once, save the look with a name, and start the next event already dressed. Your company, its parent, its sister brands and its children all see it — only whoever created it can remove it. Applying one only touches the preview: it reaches the event when you save.',
          es: 'Diséñalo una vez, guarda la apariencia con un nombre y empieza el siguiente evento ya vestido. Lo ven tu empresa, su matriz, sus marcas hermanas y sus hijas — sólo quien lo creó puede quitarlo. Aplicar uno sólo toca la previa: llega al evento cuando guardas.',
        },
        side: 'left',
        align: 'start',
      },
      {
        element: '[data-tour="dj-tabs"]',
        title: { en: 'Four tabs, four jobs', es: 'Cuatro pestañas, cuatro trabajos' },
        description: {
          en: 'LOOK is colour, logo, background and celebration. STYLE is the typography and the game. FORM is the shape of the card — including how round its top corners are, from 0 to 100. POSTER is the printable that carries the QR.',
          es: 'APARIENCIA es color, logo, fondo y celebración. ESTILO es la tipografía y el juego. FORMULARIO es la forma de la tarjeta — incluido cuánto se redondean sus esquinas de arriba, de 0 a 100. CARTEL es el impreso que lleva el QR.',
        },
        side: 'left',
        align: 'start',
      },
      {
        element: '[data-tour="dj-screens"]',
        title: { en: 'Screen and control', es: 'Pantalla y mando' },
        description: {
          en: 'Two links for the venue. The screen one goes on the TV: it only shows the poster, no buttons — the public is looking at it. The control one goes to whoever is on site: with a six-digit code they switch the event on and off from their phone, with no account. Switch it off and the screens blur the QR on their own, without anyone reloading anything.',
          es: 'Dos enlaces para el recinto. El de pantalla va al televisor: sólo enseña el cartel, sin botones — lo está mirando el público. El de mando va a quien está en la puerta: con un código de seis cifras enciende y apaga el evento desde el móvil, sin cuenta. Al apagarlo, las pantallas difuminan el QR solas, sin que nadie recargue nada.',
        },
        side: 'right',
        align: 'start',
      },
      {
        element: '[data-tour="dj-save"]',
        title: { en: 'It saves on its own', es: 'Guarda solo' },
        description: {
          en: 'A few seconds after you stop touching it, it saves — and tells you when it last did. The button stays for when you want to be sure. Saving reaches the links that are already live; deploying is only for stations that do not have this survey yet. If the server refuses a change, it stops and tells you why; your next change tries again.',
          es: 'Unos segundos después de que dejes de tocarlo, guarda — y te dice cuándo lo hizo por última vez. El botón se queda para cuando quieras asegurarte. Guardar alcanza a los enlaces que ya están en la calle; desplegar sólo hace falta para las estaciones que aún no tienen esta encuesta. Si el servidor rechaza un cambio, se detiene y te dice por qué; tu siguiente cambio lo vuelve a intentar.',
        },
        side: 'bottom',
        align: 'end',
      },
    ],
  },

  'survey-builder': {
    id: 'survey-builder',
    steps: [
      {
        title: { en: 'Design Journey', es: 'Design Journey' },
        description: {
          en: 'Build the experience guests go through at a station — a survey that flows into the prize game. No code.',
          es: 'Construye la experiencia que viven los invitados en una estación — una encuesta que desemboca en el juego de premios. Sin código.',
        },
      },
      {
        element: '[data-tour="builder-canvas"]',
        title: { en: 'Build it step by step', es: 'Constrúyelo paso a paso' },
        description: {
          en: 'Add steps and drag in components (questions, consent, the game). Reorder freely, preview the live flow, then save the template and apply it to any event from its properties panel.',
          es: 'Añade pasos y arrastra componentes (preguntas, consentimiento, el juego). Reordénalos libremente, previsualiza el flujo en vivo y luego guarda la plantilla y aplícala a cualquier evento desde su panel de propiedades.',
        },
        side: 'top',
      },
    ],
  },

  merch: {
    id: 'merch',
    steps: [
      {
        title: { en: 'My Merch', es: 'Mi Merch' },
        description: {
          en: 'This brand’s catalog of prizes & samples — the products you drop onto events and that fill each station’s prize game.',
          es: 'El catálogo de premios y muestras de esta marca — los productos que sueltas en los eventos y que llenan el juego de cada estación.',
        },
      },
      {
        element: '[data-tour="merch-create"]',
        title: { en: 'Add a product', es: 'Agrega un producto' },
        description: {
          en: 'Name it, add an image/logo and set its rating (1–5). The rating drives how often it lands in the game — higher means more frequent.',
          es: 'Ponle nombre, añade imagen/logo y define su rating (1–5). El rating controla con qué frecuencia cae en el juego — mayor significa más frecuente.',
        },
        side: 'bottom',
        align: 'end',
      },
      {
        element: '[data-tour="merch-import"]',
        title: { en: 'Bulk import', es: 'Importación masiva' },
        description: {
          en: 'Import a whole catalog at once from a spreadsheet instead of adding products one by one.',
          es: 'Importa un catálogo entero de una vez desde una hoja de cálculo en lugar de añadir productos uno por uno.',
        },
        side: 'bottom',
        align: 'end',
      },
      {
        element: '[data-tour="merch-list"]',
        title: { en: 'Your catalog', es: 'Tu catálogo' },
        description: {
          en: 'Each card shows stock levels (total, allocated, delivered). From the project planner you drag these onto events to load their games.',
          es: 'Cada tarjeta muestra el inventario (total, asignado, entregado). Desde el planificador del proyecto los arrastras a los eventos para cargar sus juegos.',
        },
        side: 'top',
      },
    ],
  },

  stock: {
    id: 'stock',
    steps: [
      {
        title: { en: 'Products & merch', es: 'Productos y merch' },
        description: {
          en: 'Your catalog of prizes and samples — the “merch” you hand out at events and that fill the prize game.',
          es: 'Tu catálogo de premios y muestras — el “merch” que entregas en los eventos y que llenan el juego de premios.',
        },
      },
      {
        element: '[data-tour="stock-create"]',
        title: { en: 'Add a product', es: 'Agrega un producto' },
        description: {
          en: 'Name it, add an image/logo, and set its rating (1–5) — the rating controls how often it lands in the game. Higher = more frequent.',
          es: 'Ponle nombre, añade imagen/logo y define su rating (1–5) — el rating controla con qué frecuencia cae en el juego. Mayor = más frecuente.',
        },
        side: 'bottom',
        align: 'end',
      },
      {
        element: '[data-tour="stock-import"]',
        title: { en: 'Bulk import', es: 'Importación masiva' },
        description: {
          en: 'Got a long catalog? Import many products at once from a spreadsheet instead of adding them one by one.',
          es: '¿Tienes un catálogo largo? Importa muchos productos a la vez desde una hoja de cálculo en lugar de uno por uno.',
        },
        side: 'bottom',
        align: 'end',
      },
    ],
  },

  stations: {
    id: 'stations',
    steps: [
      {
        title: { en: 'Stations', es: 'Estaciones' },
        description: {
          en: 'Every touchpoint across the brand in one place. Stations are created inside events (Plan tab); here you manage them all.',
          es: 'Todos los puntos de contacto de la marca en un lugar. Las estaciones se crean dentro de los eventos (pestaña Planificar); aquí los gestionas todos.',
        },
      },
      {
        element: '[data-tour="stations-list"]',
        title: { en: 'Manage a station', es: 'Gestiona una estación' },
        description: {
          en: 'Open a station to allocate its inventory and read responses, or print its QR code for the booth. Each row links straight to those actions.',
          es: 'Abre una estación para asignar su inventario y leer respuestas, o imprime su código QR para el stand. Cada fila enlaza directo a esas acciones.',
        },
        side: 'top',
      },
    ],
  },

  'station-detail': {
    id: 'station-detail',
    steps: [
      {
        title: { en: 'Station detail', es: 'Detalle de estación' },
        description: {
          en: 'The control panel for one station — its inventory, its responses and its live dashboard.',
          es: 'El panel de control de una estación — su inventario, sus respuestas y su tablero en vivo.',
        },
      },
      {
        element: '[data-tour="station-tabs"]',
        title: { en: 'Inventory · Responses · Dashboard', es: 'Inventario · Respuestas · Tablero' },
        description: {
          en: 'INVENTORY tunes the prize slices on this station’s game. RESPONSES shows captured survey data. DASHBOARD is its live performance view.',
          es: 'INVENTARIO ajusta los slices de premios del juego de esta estación. RESPUESTAS muestra los datos capturados. TABLERO es su vista de rendimiento en vivo.',
        },
        side: 'bottom',
      },
    ],
  },

  insights: {
    id: 'insights',
    steps: [
      {
        title: { en: 'Insights', es: 'Métricas' },
        description: {
          en: 'Aggregated performance for the active brand — turn raw activity into decisions.',
          es: 'Rendimiento agregado de la marca activa — convierte la actividad en decisiones.',
        },
      },
      {
        element: '[data-tour="insights-filters"]',
        title: { en: 'Filter the data', es: 'Filtra los datos' },
        description: {
          en: 'Narrow by project, event and date range. Every tab below responds to these filters.',
          es: 'Acota por proyecto, evento y rango de fechas. Cada pestaña de abajo responde a estos filtros.',
        },
        side: 'bottom',
      },
      {
        element: '[data-tour="insights-tabs"]',
        title: { en: 'Explore by lens', es: 'Explora por enfoque' },
        description: {
          en: 'Each tab tells a different story about the same filtered data — engagement, inventory, audience and more.',
          es: 'Cada pestaña cuenta una historia distinta sobre los mismos datos filtrados — engagement, inventario, audiencia y más.',
        },
        side: 'bottom',
      },
    ],
  },
};

/**
 * Map a router pathname to the tour that should run on it. Returns null when
 * the page has no tour. Patterns are ordered most-specific first.
 */
export function tourIdForPath(pathname: string, search = ''): string | null {
  // El Design Journey no es una ruta: es una pestaña, y la pestaña vive en la
  // query (`?tab=survey` en un evento, `?tab=journey` en un proyecto). Sin
  // mirarla, la brújula de la barra ofrecería el recorrido del evento entero
  // mientras se está delante del panel de diseño.
  const tab = new URLSearchParams(search).get('tab');
  if (
    (tab === 'survey' && /^\/projects\/[^/]+\/events\/[^/]+$/.test(pathname)) ||
    (tab === 'journey' && /^\/projects\/[^/]+$/.test(pathname))
  ) {
    return 'design-journey';
  }
  if (/^\/projects\/[^/]+\/events\/[^/]+$/.test(pathname)) return 'event-detail';
  if (/^\/projects\/[^/]+$/.test(pathname)) return 'project-detail';
  if (/^\/brand\/[^/]+\/projects$/.test(pathname)) return 'projects';
  if (/^\/surveys\/builder(\/[^/]+)?$/.test(pathname)) return 'survey-builder';
  if (/^\/stations\/[^/]+$/.test(pathname)) return 'station-detail';
  if (pathname === '/stations') return 'stations';
  if (pathname === '/stock') return 'stock';
  if (/^\/brand\/[^/]+\/xotskies$/.test(pathname)) return 'merch';
  if (pathname === '/insights') return 'insights';
  if (pathname === '/' || /^\/brand\/[^/]+$/.test(pathname)) return 'home';
  return null;
}
